// Phase 3A — client journey-state hook.
//
// Combines the existing Supabase session and entitlement provider with a
// first-party "seen before" flag. No profiling, no third-party personalization
// library, no PII. Renders a stable anonymous-first-visit state during SSR and
// the first paint, so there is no layout shift when the real state resolves.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useEntitlement } from "@/hooks/use-entitlement";
import { resolveVisitorState, stageForVisitor, type VisitorState, type JourneyStage } from "@/lib/journey";
import { track } from "@/lib/analytics";

const SEEN_KEY = "ka.journey.seen.v1";
const RETURN_LOGGED_KEY = "ka.journey.returnLogged.v1";

function readReturning(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* private mode */
  }
}

export interface JourneyState {
  visitor: VisitorState;
  stage: JourneyStage;
  signedIn: boolean;
  isMember: boolean;
  returning: boolean;
  /** True until the real session/entitlement state is known. */
  loading: boolean;
}

export function useJourney(): JourneyState {
  const { isMember, loading: entLoading } = useEntitlement();
  const [signedIn, setSignedIn] = useState(false);
  const [returning, setReturning] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const wasReturning = readReturning();
    setReturning(wasReturning);
    markSeen();
    if (wasReturning) {
      try {
        if (window.sessionStorage.getItem(RETURN_LOGGED_KEY) !== "1") {
          window.sessionStorage.setItem(RETURN_LOGGED_KEY, "1");
          track("journey_returning_visit");
        }
      } catch {
        /* private mode */
      }
    }

    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSignedIn(Boolean(data.session));
      setAuthLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(Boolean(session));
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const visitor = resolveVisitorState({ signedIn, isMember, returning });
  return {
    visitor,
    stage: stageForVisitor(visitor),
    signedIn,
    isMember,
    returning,
    loading: authLoading || entLoading,
  };
}
